import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionsResponse } from '@tt/shared';
import type { ComboboxItem } from '@/components/combobox';

/** Turns the /me/options payload into combobox items with recent-first ordering. */
export function useOptionItems(options: OptionsResponse | undefined) {
  const { t } = useTranslation();
  return useMemo(() => {
    const projects: ComboboxItem[] = (options?.projects ?? []).map((p) => ({
      value: p.id,
      label: p.name,
      hint: p.status === 'ARCHIVED' ? t('time.archived') : (p.code ?? undefined),
      color: p.color,
      disabled: p.status === 'ARCHIVED',
    }));
    const toItem = (a: OptionsResponse['activities'][number]): ComboboxItem => ({
      value: a.id,
      label: a.name,
      hint:
        a.status === 'ARCHIVED'
          ? t('time.archived')
          : a.type === 'PERSONAL'
            ? t('time.personal')
            : undefined,
      disabled: a.status === 'ARCHIVED',
    });
    const recent = options?.recent ?? [];
    const uniq = (list: string[]) => Array.from(new Set(list)).slice(0, 5);
    return {
      projects,
      /**
       * Activities of one project. There is deliberately no flat list of all of them: an activity
       * belongs to exactly one project (design §7.3), so offering them all would offer a choice
       * the server rejects — and after migration 0005 split the shared ones, the flat list would
       * be the same handful of names repeated once per project with nothing to tell them apart.
       *
       * No project chosen yet means nothing to choose from, which is why the callers disable the
       * activity picker until one is.
       */
      activitiesFor: (projectId: string | null): ComboboxItem[] =>
        projectId === null
          ? []
          : (options?.activities ?? []).filter((a) => a.projectId === projectId).map(toItem),
      recentProjects: uniq(recent.map((r) => r.projectId)),
      /** Activities most recently used with a given project, first. */
      recentActivitiesFor: (projectId: string | null) =>
        uniq(
          recent.filter((r) => !projectId || r.projectId === projectId).map((r) => r.activityId),
        ),
    };
  }, [options, t]);
}
